import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import { SubmitHandlerWithDirtyFields } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getUpdateChargePointOcppConfigurationDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/update-charge-point-ocpp-configuration.form-default-values';
import { UpdateChargePointOcppConfigurationModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/inputs/update-charge-point-ocpp-configuration.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useCpmsBoUpdateChargePointOcppConfigurationMutation } from '@app/core/service/cpms-bo-charge-point-ocpp.service';

import {
  UpdateChargePointOcppConfigurationContextProvider,
  useChargePointContext,
  useChargePointOcppConfigurationContext,
} from '../charge-point.context';
import { ChargePointOcppConfigurationUpdateFormController } from './charge-point-ocpp-configuration-update-form/charge-point-ocpp-configuration-update-form.controller';

/**
 * A configuration entry is registered as a whole object, so react-hook-form
 * marks it dirty either as a boolean or as a map of its own dirty leaves.
 */
const isConfigurationDirty = (
  dirtyConfiguration: boolean | Record<string, boolean> | undefined,
): boolean =>
  typeof dirtyConfiguration === 'boolean'
    ? dirtyConfiguration
    : Object.values(dirtyConfiguration ?? {}).some(Boolean);

interface ChargePointOcppConfigurationUpdateControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
  isReadonly: boolean;
}

export const ChargePointOcppConfigurationUpdateController = ({
  onCancel,
  onSuccess,
  isReadonly,
}: ChargePointOcppConfigurationUpdateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const chargePoint = useChargePointContext();
  const chargePointOcppConfiguration = useChargePointOcppConfigurationContext();
  const [updateChargePointOcppConfigurationMutation] =
    useCpmsBoUpdateChargePointOcppConfigurationMutation();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<
    UpdateChargePointOcppConfigurationModel & { chargePointId: string }
  > = (errors) => {
    Logger.error(errors);
    toast.error(
      t('$.charge-point-ocpp-configuration-update.error', {
        error: t('global.unknown'),
      }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    UpdateChargePointOcppConfigurationModel & { chargePointId: string }
  > = (fieldValues, _, dirtyFields) => {
    setMultiStepFormState(MultiStepFormState.Loading);

    // Readonly configuration keys are rendered as disabled fields, and
    // react-hook-form replaces every disabled field by `undefined` on submit.
    // Keeping only the modified entries drops those holes and avoids sending
    // untouched keys to the charge point.
    const dirtyConfigurations = fieldValues.configurations.filter(
      (configuration, index) =>
        !!configuration &&
        isConfigurationDirty(dirtyFields.configurations?.[index]),
    );

    if (dirtyConfigurations.length === 0) {
      setMultiStepFormState(MultiStepFormState.Default);
      return;
    }

    updateChargePointOcppConfigurationMutation({
      chargePointId: fieldValues.chargePointId,
      configurations: dirtyConfigurations,
    })
      .unwrap()
      .then(() => {
        toast.success(t('$.charge-point-ocpp-configuration-update.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(
          t('$.charge-point-ocpp-configuration-update.error', {
            error: errorMessage,
          }),
        );
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getUpdateChargePointOcppConfigurationDefaultValues(
    chargePoint.id,
    chargePointOcppConfiguration,
  );

  return (
    <UpdateChargePointOcppConfigurationContextProvider data={data}>
      <ChargePointOcppConfigurationUpdateFormController
        isReadonly={isReadonly}
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </UpdateChargePointOcppConfigurationContextProvider>
  );
};
